import { value } from '@takeshape/util';
import { CURRENT_OPEN_API_TRANSFORM_VERSION } from "../constants.js";
import { hasEveryKey } from "../util/has.js";
import { endpointWithPath, getHealthCheck } from "../util/health.js";
import { getInitialData } from "../util/initial-data.js";
import { fixObfuscatedProp } from "../util/props.js";
const providerId = 'judgeMe';
const serviceProvider = {
    name: providerId,
    title: 'Judge.me',
    description: 'Boost Sales With Product Reviews! Join 100k+ stores using Judge.me to generate customer reviews for your e-commerce store.',
    categories: ['ecommerce', 'reviews'],
    links: [{ title: 'Homepage', url: 'https://judge.me/' }],
    serviceType: 'openapi',
    introspectionType: 'openapi',
    createId: value('judge.me'),
    getFormSchema: value({
        type: 'object',
        required: ['title', 'id', 'namespace', 'shopDomain', 'authentication'],
        properties: {
            title: {
                title: 'Name',
                type: 'string',
                default: 'Judge.me'
            },
            id: {
                title: 'Slug',
                type: 'string',
                pattern: '[0-9a-z_-]+',
                default: 'judge-me'
            },
            namespace: {
                title: 'Namespace',
                type: 'string',
                pattern: '^[A-Z][A-Za-z]+$',
                default: 'JudgeMe'
            },
            shopDomain: {
                title: 'Shop Domain',
                type: 'string',
                format: 'hostname'
            },
            authentication: {
                title: 'Authentication',
                type: 'object',
                properties: {
                    token: {
                        title: 'Private API Key',
                        type: 'string',
                        '@sensitive': true
                    }
                },
                required: ['token']
            }
        }
    }),
    getFormConfig: value({
        order: ['title', 'id', 'namespace', 'shopDomain', 'authentication'],
        properties: {
            title: {
                widget: 'singleLineText',
                instructions: 'A human-readable title for this service.'
            },
            id: {
                widget: 'singleLineText',
                instructions: 'The human-readable unique identifier for this service. Only lowercase letters, numbers, hyphen, and underscore are allowed.'
            },
            namespace: {
                widget: 'singleLineText',
                instructions: 'A namespace to use for any generated types or queries. For example, if you enter `Foo` your types will be named `Foo_Bar`.'
            },
            shopDomain: {
                widget: 'singleLineText',
                instructions: 'The domain name of your Shopify, BigCommerce, Woo Commerce or Squarespace store.'
            },
            authentication: {
                properties: {
                    token: {
                        widget: 'sensitiveField',
                        instructions: 'To get your private api key go to your [judge.me settings](https://judge.me/settings?jump_to=judge.me+api).'
                    }
                },
                order: ['token']
            }
        }
    }),
    getConfig(formData) {
        const shopDomain = formData.shopDomain;
        const config = {
            id: formData.id,
            title: formData.title ?? 'Judge.me',
            provider: providerId,
            serviceType: 'openapi',
            authenticationType: 'searchParams',
            namespace: formData.namespace,
            options: {
                endpoint: 'https://judge.me/api/v1',
                spec: 'https://raw.githubusercontent.com/takeshape/openapi-specs/main/specs/judge.me/v1.yml',
                transformVersion: CURRENT_OPEN_API_TRANSFORM_VERSION,
                useRestfulNames: true,
                searchParams: {
                    shop_domain: shopDomain
                }
            }
        };
        const apiKey = fixObfuscatedProp(formData.authentication?.token);
        if (apiKey) {
            config.authentication = {
                type: 'searchParams',
                params: [{ name: 'api_token', value: apiKey }]
            };
        }
        return config;
    },
    getInitialData(serviceConfig) {
        const initialData = getInitialData(serviceProvider, serviceConfig);
        // Relocate this value for editing
        initialData.shopDomain = serviceConfig?.options?.searchParams.shop_domain;
        return initialData;
    },
    isCompleteConfig: hasEveryKey([
        'authentication',
        'options.endpoint',
        'options.spec',
        'options.searchParams.shop_domain'
    ]),
    getHealthCheck: getHealthCheck({
        checkName: 'ping',
        endpoint: endpointWithPath('/widgets/all_reviews_count')
    })
};
export default serviceProvider;
